import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

import Sidebar from "../components/Sidebar";
import SearchableDropdown from "../components/SearchableDropdown";
import { useAuth } from "../context/AuthContext";

const COLORS = {
    navy: "#00205B",
    lightBlue: "#008CDE",
    deepGreen: "#00594C",
    orange: "#EF7C00",
    steelGrey: "#5E6A71",
    coolGrey: "#D9D9D6",
    lightTint: "#F4F5F6",
    error: "#E65100",
};

const createHeaders = (token) => ({
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
});

const inputStyle = {
    width: "100%",
    height: "38px",
    borderRadius: "10px",
    border: `1px solid ${COLORS.coolGrey}`,
    padding: "0 12px",
    fontSize: "13px",
    outline: "none",
};

const buttonStyle = {
    height: "38px",
    borderRadius: "10px",
    border: "none",
    padding: "0 14px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 600,
};

const panelStyle = {
    background: "#fff",
    border: `1px solid ${COLORS.coolGrey}`,
    borderRadius: "16px",
    padding: "18px",
    marginBottom: "18px",
};

const sectionTitle = {
    marginTop: 0,
    marginBottom: "12px",
    color: COLORS.navy,
    fontSize: "18px",
};

const GridField = ({ label, children }) => (
    <div>
        <label style={{ fontSize: "12px", fontWeight: 700, color: COLORS.navy }}>{label}</label>
        {children}
    </div>
);

const apiGet = async (url, headers) => {
    const res = await axios.get(url, { headers });
    return res.data;
};

const apiPost = async (url, payload, headers) => {
    const res = await axios.post(url, payload, { headers });
    return res.data;
};

function DeveloperControl() {
    const { t } = useTranslation();
    const { token } = useAuth();
    const API = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080/api";
    const headers = useMemo(() => createHeaders(token), [token]);

    const [mode, setMode] = useState("");
    const [error, setError] = useState("");

    const [pages, setPages] = useState([]);
    const [pageFields, setPageFields] = useState([]);
    const [selectedPageName, setSelectedPageName] = useState("");
    const [selectedPage, setSelectedPage] = useState(null);
    const [editablePage, setEditablePage] = useState(null);
    const [isEditingPage, setIsEditingPage] = useState(false);

    const [roles, setRoles] = useState([]);
    const [rolePages, setRolePages] = useState([]);
    const [roleFields, setRoleFields] = useState([]);
    const [selectedRole, setSelectedRole] = useState("");
    const [selectedRolePage, setSelectedRolePage] = useState("");
    const [rolePageData, setRolePageData] = useState(null);
    const [isEditingRolePage, setIsEditingRolePage] = useState(false);

    const [groups, setGroups] = useState([]);
    const [showGroupManager, setShowGroupManager] = useState(false);
    const [groupPageId, setGroupPageId] = useState("");
    const [groupPageName, setGroupPageName] = useState("");
    const [groupFields, setGroupFields] = useState([]);
    const [groupDisplayName, setGroupDisplayName] = useState("");
    const [groupDescription, setGroupDescription] = useState("");
    const [editingGroupId, setEditingGroupId] = useState(null);

    const [loading, setLoading] = useState({
        pages: false,
        fields: false,
        roles: false,
        rolePages: false,
        roleFields: false,
        pageSave: false,
        rolePageSave: false,
        fieldSaveId: null,
        roleFieldSaveName: null,
        groups: false,
        savingGroup: false,
    });

    const setLoad = (key, value) => setLoading((p) => ({ ...p, [key]: value }));

    const fetchPages = async () => {
        setLoad("pages", true);
        try {
            const data = await apiGet(`${API}/access-control/get-all-pages`, headers);
            setPages(Array.isArray(data?.data) ? data.data : []);
        } catch (err) {
            setPages([]);
            setError(err?.response?.data?.message || err.message || "Failed to fetch pages");
        } finally {
            setLoad("pages", false);
        }
    };

    const fetchFieldsByPage = async (pageId) => {
        if (!pageId) return setPageFields([]);
        setLoad("fields", true);
        try {
            const data = await apiGet(`${API}/access-control/get-fields-by-page?pageId=${pageId}`, headers);
            setPageFields(Array.isArray(data?.data) ? data.data : []);
        } catch (err) {
            setPageFields([]);
            setError(err?.response?.data?.message || err.message || "Failed to fetch fields");
        } finally {
            setLoad("fields", false);
        }
    };

    const fetchRoles = async () => {
        setLoad("roles", true);
        try {
            const data = await apiGet(`${API}/access-control/all-roles`, headers);
            setRoles(Array.isArray(data?.data) ? data.data : Array.isArray(data?.roles) ? data.roles : []);
        } catch {
            setRoles([]);
        } finally {
            setLoad("roles", false);
        }
    };

    const fetchRolePages = async (role) => {
        if (!role) return setRolePages([]);
        setLoad("rolePages", true);
        try {
            const snake = role.trim().toLowerCase().replace(/[\s-]+/g, "_");
            const data = await apiGet(`${API}/access-control/get-rbac-page-by-role?role=${snake}`, headers);
            setRolePages(Array.isArray(data?.data) ? data.data : []);
        } catch (err) {
            setRolePages([]);
            setError(err?.response?.data?.message || err.message || "Failed to fetch role pages");
        } finally {
            setLoad("rolePages", false);
        }
    };

    const fetchRoleFields = async (role, pageName) => {
        if (!role || !pageName) return setRoleFields([]);
        setLoad("roleFields", true);
        try {
            const snake = role.trim().toLowerCase().replace(/[\s-]+/g, "_");
            const data = await apiGet(
                `${API}/access-control/get-rbac-field-by-role?role=${encodeURIComponent(snake)}&pageName=${encodeURIComponent(pageName)}`,
                headers
            );
            setRoleFields(Array.isArray(data?.data) ? data.data : []);
        } catch (err) {
            setRoleFields([]);
            setError(err?.response?.data?.message || err.message || "Failed to fetch role fields");
        } finally {
            setLoad("roleFields", false);
        }
    };

    const fetchGroups = async () => {
        setLoad("groups", true);
        try {
            const data = await apiGet(`${API}/access-control/get-all-groups`, headers);
            setGroups(Array.isArray(data?.details) ? data.details : []);
        } catch (err) {
            setGroups([]);
            setError(err?.response?.data?.message || err.message || "Failed to fetch groups");
        } finally {
            setLoad("groups", false);
        }
    };

    useEffect(() => {
        if (!token) return;
        fetchPages();
        fetchRoles();
        fetchGroups();
    }, [token]);

    const pageOptions = useMemo(() => pages.map((p) => p.display_name || p.page_name).filter(Boolean), [pages]);
    const roleOptions = useMemo(
        () => roles.map((r) => (typeof r === "string" ? r : r.role || r.name || r.display_name)).filter(Boolean),
        [roles]
    );
    const rolePageOptions = useMemo(
        () => rolePages.map((p) => (typeof p === "string" ? p : p.display_name || p.page_name || p.name)).filter(Boolean),
        [rolePages]
    );

    const resetGroupForm = () => {
        setGroupPageId("");
        setGroupPageName("");
        setGroupFields([]);
        setGroupDisplayName("");
        setGroupDescription("");
        setEditingGroupId(null);
        setPageFields([]);
    };

    const resetAllGroupPanel = () => {
        setShowGroupManager(false);
        resetGroupForm();
    };

    const handlePageChange = async (e) => {
        const name = e.target.value;
        setSelectedPageName(name);
        const page = pages.find((p) => (p.display_name || p.page_name) === name) || null;
        setSelectedPage(page);
        setEditablePage(page ? { ...page } : null);
        setIsEditingPage(false);
        await fetchFieldsByPage(page?.id);
    };

    const handleRoleChange = async (e) => {
        const role = e.target.value;
        setSelectedRole(role);
        setSelectedRolePage("");
        setRolePages([]);
        setRoleFields([]);
        setRolePageData(null);
        setIsEditingRolePage(false);
        await fetchRolePages(role);
    };

    const handleRolePageChange = async (e) => {
        const pageName = e.target.value;
        setSelectedRolePage(pageName);
        const page = rolePages.find((p) => (typeof p === "string" ? p : p.display_name || p.page_name || p.name) === pageName);
        setRolePageData(
            page
                ? { ...page }
                : { page_name: pageName, display_name: pageName, is_active: true }
        );
        setIsEditingRolePage(false);
        await fetchRoleFields(selectedRole, pageName);
    };

    const savePage = async () => {
        if (!editablePage?.id) return;
        setLoad("pageSave", true);
        try {
            await apiPost(
                `${API}/access-control/update-page-by-id?pageId=${editablePage.id}`,
                {
                    page_name: editablePage.page_name || "",
                    display_name: editablePage.display_name || "",
                    is_active: !!editablePage.is_active,
                },
                headers
            );
            Swal.fire("Success", t("Page updated successfully"), "success");
            setPages((prev) => prev.map((p) => (p.id === editablePage.id ? { ...p, ...editablePage } : p)));
            setIsEditingPage(false);
        } catch (err) {
            Swal.fire("Error", err?.response?.data?.message || err.message || "Failed to save page", "error");
        } finally {
            setLoad("pageSave", false);
        }
    };

    const saveRolePage = async () => {
        if (!selectedRole || !selectedRolePage) return;
        setLoad("rolePageSave", true);
        try {
            await apiPost(
                `${API}/access-control/update-role-page-permissions`,
                {
                    role: selectedRole.trim().toLowerCase().replace(/[\s-]+/g, "_"),
                    page_name: rolePageData?.page_name || selectedRolePage,
                    display_name: rolePageData?.display_name || selectedRolePage,
                    is_active: !!rolePageData?.is_active,
                },
                headers
            );
            Swal.fire("Success", t("Role page permission updated successfully"), "success");
            setIsEditingRolePage(false);
        } catch (err) {
            Swal.fire("Error", err?.response?.data?.message || err.message || "Failed to save role page", "error");
        } finally {
            setLoad("rolePageSave", false);
        }
    };

    const saveField = async (field) => {
        setLoad("fieldSaveId", field.id);
        try {
            await apiPost(
                `${API}/access-control/update-field-by-id?fieldId=${field.id}`,
                {
                    field_name: field.field_name || "",
                    display_name: field.display_name || "",
                    is_active: !!field.is_active,
                },
                headers
            );
            Swal.fire("Success", t("Field updated successfully"), "success");
        } catch (err) {
            Swal.fire("Error", err?.response?.data?.message || err.message || "Failed to save field", "error");
        } finally {
            setLoad("fieldSaveId", null);
        }
    };

    const saveRoleField = async (field) => {
        const fieldName = field.field_name || field.name;
        setLoad("roleFieldSaveName", fieldName);
        try {
            const snake = selectedRole.trim().toLowerCase().replace(/[\s-]+/g, "_");
            await apiPost(
                `${API}/access-control/update-role-field-permissions`,
                {
                    role: snake,
                    page_name: selectedRolePage,
                    field_name: fieldName,
                    is_active: !!field.is_active,
                },
                headers
            );
            Swal.fire("Success", t("Role field permission updated successfully"), "success");
            await fetchRoleFields(selectedRole, selectedRolePage);
        } catch (err) {
            Swal.fire("Error", err?.response?.data?.message || err.message || "Failed to save role field", "error");
        } finally {
            setLoad("roleFieldSaveName", null);
        }
    };

    const toggleGroupField = (id) => {
        setGroupFields((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const handleSaveGroup = async () => {
        if (!groupPageId || !groupDisplayName.trim() || groupFields.length === 0) {
            Swal.fire("Error", "Please fill all required fields", "error");
            return;
        }

        setLoad("savingGroup", true);
        try {
            const endpoint = editingGroupId
                ? `${API}/access-control/update-field-groups`
                : `${API}/access-control/create-field-groups`;

            const payload = editingGroupId
                ? {
                    id: editingGroupId,
                    groups: groupFields,
                    display_name: groupDisplayName,
                    is_active: true,
                }
                : {
                    page_id: Number(groupPageId),
                    groups: groupFields,
                    display_name: groupDisplayName,
                    description: groupDescription || "",
                    is_active: true,
                };

            await apiPost(endpoint, payload, headers);
            Swal.fire(
                "Success",
                editingGroupId ? t("Field group updated successfully") : t("Field group created successfully"),
                "success"
            );
            await fetchGroups();
            resetGroupForm();
        } catch (err) {
            Swal.fire("Error", err?.response?.data?.message || err.message || "Failed to save group", "error");
        } finally {
            setLoad("savingGroup", false);
        }
    };

    const handleEditGroup = async (group) => {
        setEditingGroupId(group.id);
        setGroupPageId(String(group.page_id || ""));
        setGroupPageName(group.page_metadata?.display_name || group.page_metadata?.page_name || "");
        setGroupFields(Array.isArray(group.groups) ? group.groups : []);
        setGroupDisplayName(group.display_name || "");
        setGroupDescription(group.description || "");
        setShowGroupManager(true);
        await fetchFieldsByPage(group.page_id);
    };

    const GroupActionButton = ({ onClick, children, color = COLORS.deepGreen, disabled }) => (
        <button
            style={{
                ...buttonStyle,
                background: color,
                color: "#fff",
                opacity: disabled ? 0.7 : 1,
            }}
            onClick={onClick}
            disabled={disabled}
        >
            {children}
        </button>
    );

    const renderFieldChips = (fields) => (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px" }}>
            {fields.map((field) => (
                <label
                    key={field.id}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "10px 12px",
                        borderRadius: "10px",
                        border: `1px solid ${groupFields.includes(field.id) ? COLORS.lightBlue : COLORS.coolGrey}`,
                        background: groupFields.includes(field.id) ? COLORS.lightTint : "#fff",
                        cursor: "pointer",
                    }}
                >
                    <input
                        type="checkbox"
                        checked={groupFields.includes(field.id)}
                        onChange={() => toggleGroupField(field.id)}
                    />
                    <span>{field.display_name || field.field_name || field.name}</span>
                    <span style={{ marginLeft: "auto", fontSize: "11px", color: COLORS.steelGrey }}>
                        #{field.id}
                    </span>
                </label>
            ))}
        </div>
    );

    return (
        <Sidebar title={t("Developer Control")}>
            <div style={{ padding: "16px" }}>
                <div
                    style={{
                        background: COLORS.navy,
                        color: "#fff",
                        padding: "18px 20px",
                        borderRadius: "16px",
                        marginBottom: "18px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                    }}
                >
                    <div>
                        <h2 style={{ margin: 0, fontSize: "24px" }}>{t("Developer Control")}</h2>
                        <p style={{ margin: "6px 0 0", opacity: 0.9 }}>
                            {t("Manage page fields, role fields, and field groups")}
                        </p>
                    </div>

                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        <button
                            style={{
                                ...buttonStyle,
                                background: showGroupManager ? COLORS.deepGreen : COLORS.lightTint,
                                color: showGroupManager ? "#fff" : COLORS.navy,
                            }}
                            onClick={() => setShowGroupManager((prev) => !prev)}
                        >
                            {showGroupManager ? t("Hide Group Manager") : t("Show Group Manager")}
                        </button>
                    </div>
                </div>

                {error ? (
                    <div
                        style={{
                            marginBottom: "16px",
                            padding: "12px 14px",
                            borderRadius: "10px",
                            background: "#fff3f0",
                            border: `1px solid ${COLORS.orange}`,
                            color: COLORS.error,
                        }}
                    >
                        {error}
                    </div>
                ) : null}

                <div style={panelStyle}>
                    <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                        <button
                            onClick={() => setMode("page")}
                            style={{
                                ...buttonStyle,
                                background: mode === "page" ? COLORS.navy : COLORS.lightTint,
                                color: mode === "page" ? "#fff" : COLORS.navy,
                            }}
                        >
                            {t("Page-wise field controls")}
                        </button>
                        <button
                            onClick={() => setMode("role")}
                            style={{
                                ...buttonStyle,
                                background: mode === "role" ? COLORS.navy : COLORS.lightTint,
                                color: mode === "role" ? "#fff" : COLORS.navy,
                            }}
                        >
                            {t("Role-wise field controls")}
                        </button>
                    </div>
                </div>

                {showGroupManager && (
                    <>
                        <div style={panelStyle}>
                            <h3 style={sectionTitle}>
                                {editingGroupId ? t("Update Field Group") : t("Create Field Group")}
                            </h3>

                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                                    gap: "14px",
                                }}
                            >
                                <GridField label={t("Page")}>
                                    <SearchableDropdown
                                        options={pageOptions}
                                        value={groupPageName}
                                        onChange={async (e) => {
                                            const name = e.target.value;
                                            setGroupPageName(name);
                                            const page = pages.find((p) => (p.display_name || p.page_name) === name) || null;
                                            setGroupPageId(page?.id ? String(page.id) : "");
                                            setGroupFields([]);
                                            await fetchFieldsByPage(page?.id);
                                        }}
                                        placeholder="Select page"
                                        isLoading={loading.pages}
                                    />
                                </GridField>

                                <GridField label={t("Display Name")}>
                                    <input
                                        style={inputStyle}
                                        value={groupDisplayName}
                                        onChange={(e) => setGroupDisplayName(e.target.value)}
                                        placeholder="Enter group display name"
                                    />
                                </GridField>

                                <GridField label={t("Description")}>
                                    <input
                                        style={inputStyle}
                                        value={groupDescription}
                                        onChange={(e) => setGroupDescription(e.target.value)}
                                        placeholder="Enter description"
                                        disabled={!!editingGroupId}
                                    />
                                </GridField>

                                <div style={{ display: "flex", alignItems: "flex-end", gap: "10px" }}>
                                    <GroupActionButton onClick={handleSaveGroup} color={editingGroupId ? COLORS.deepGreen : COLORS.lightBlue} disabled={loading.savingGroup}>
                                        {loading.savingGroup
                                            ? t("Saving...")
                                            : editingGroupId
                                                ? t("Update Group")
                                                : t("Create Group")}
                                    </GroupActionButton>

                                    {editingGroupId ? (
                                        <button
                                            style={{
                                                ...buttonStyle,
                                                background: COLORS.lightTint,
                                                color: COLORS.navy,
                                                border: `1px solid ${COLORS.coolGrey}`,
                                            }}
                                            onClick={resetGroupForm}
                                        >
                                            {t("Cancel")}
                                        </button>
                                    ) : null}
                                </div>
                            </div>

                            <div style={{ marginTop: "16px" }}>
                                <h4 style={{ marginBottom: "10px", color: COLORS.navy }}>{t("Select Fields")}</h4>
                                {groupPageId ? (
                                    loading.fields ? (
                                        <div>{t("Loading fields...")}</div>
                                    ) : pageFields.length === 0 ? (
                                        <div style={{ color: COLORS.steelGrey }}>{t("No fields found for this page")}</div>
                                    ) : (
                                        renderFieldChips(pageFields)
                                    )
                                ) : (
                                    <div style={{ color: COLORS.steelGrey }}>{t("Please select a page first.")}</div>
                                )}
                            </div>
                        </div>

                        <div style={panelStyle}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                                <h3 style={sectionTitle}>{t("Existing Field Groups")}</h3>
                                <button
                                    style={{
                                        ...buttonStyle,
                                        background: COLORS.lightTint,
                                        color: COLORS.navy,
                                        border: `1px solid ${COLORS.coolGrey}`,
                                    }}
                                    onClick={fetchGroups}
                                >
                                    {t("Refresh")}
                                </button>
                            </div>

                            <div style={{ marginTop: "14px" }}>
                                {loading.groups ? (
                                    <div>{t("Loading groups...")}</div>
                                ) : groups.length === 0 ? (
                                    <div style={{ color: COLORS.steelGrey }}>{t("No groups found.")}</div>
                                ) : (
                                    <div style={{ display: "grid", gap: "12px" }}>
                                        {groups.map((group) => (
                                            <div
                                                key={group.id}
                                                style={{
                                                    border: `1px solid ${COLORS.coolGrey}`,
                                                    borderRadius: "12px",
                                                    padding: "14px",
                                                    background: group.is_active ? "#fff" : COLORS.lightTint,
                                                }}
                                            >
                                                <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                                                    <div>
                                                        <div style={{ fontWeight: 700, color: COLORS.navy }}>
                                                            {group.display_name}
                                                        </div>
                                                        <div style={{ fontSize: "12px", color: COLORS.steelGrey, marginTop: "4px" }}>
                                                            Group ID: {group.id} | Page ID: {group.page_id} | Active: {String(group.is_active)}
                                                        </div>
                                                        <div style={{ fontSize: "12px", color: COLORS.steelGrey, marginTop: "4px" }}>
                                                            Fields: {(group.fields || []).map((f) => f.display_name || f.field_name || f.id).join(", ")}
                                                        </div>
                                                    </div>

                                                    <GroupActionButton onClick={() => handleEditGroup(group)} color={COLORS.deepGreen}>
                                                        {t("Edit")}
                                                    </GroupActionButton>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {mode === "page" && (
                    <div style={panelStyle}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                            <div style={{ minWidth: "260px", flex: "1" }}>
                                <GridField label={t("Page")}>
                                    <SearchableDropdown
                                        options={pageOptions}
                                        value={selectedPageName}
                                        onChange={handlePageChange}
                                        placeholder="Select page"
                                        isLoading={loading.pages}
                                    />
                                </GridField>
                            </div>

                            {selectedPage ? (
                                <div style={{ display: "flex", gap: "10px", alignItems: "flex-end", flexWrap: "wrap" }}>
                                    <div style={{ minWidth: "180px" }}>
                                        <GridField label={t("Page Name")}>
                                            <input
                                                style={inputStyle}
                                                value={editablePage?.page_name || ""}
                                                disabled={!isEditingPage}
                                                onChange={(e) =>
                                                    setEditablePage((prev) => ({ ...prev, page_name: e.target.value }))
                                                }
                                            />
                                        </GridField>
                                    </div>

                                    <div style={{ minWidth: "180px" }}>
                                        <GridField label={t("Display Name")}>
                                            <input
                                                style={inputStyle}
                                                value={editablePage?.display_name || ""}
                                                disabled={!isEditingPage}
                                                onChange={(e) =>
                                                    setEditablePage((prev) => ({ ...prev, display_name: e.target.value }))
                                                }
                                            />
                                        </GridField>
                                    </div>

                                    <div style={{ minWidth: "80px" }}>
                                        <GridField label={t("Status")}>
                                            <div style={{ height: "38px", display: "flex", alignItems: "center" }}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!editablePage?.is_active}
                                                    disabled={!isEditingPage}
                                                    onChange={() =>
                                                        setEditablePage((prev) => ({
                                                            ...prev,
                                                            is_active: !prev?.is_active,
                                                        }))
                                                    }
                                                />
                                            </div>
                                        </GridField>
                                    </div>

                                    <div style={{ minWidth: "110px" }}>
                                        <GridField label={t("Action")}>
                                            <button
                                                style={{
                                                    ...buttonStyle,
                                                    background: isEditingPage ? COLORS.lightBlue : COLORS.lightTint,
                                                    color: isEditingPage ? "#fff" : COLORS.navy,
                                                    width: "100%",
                                                }}
                                                onClick={isEditingPage ? savePage : () => setIsEditingPage(true)}
                                            >
                                                {loading.pageSave ? t("Saving...") : isEditingPage ? t("Save") : t("Edit")}
                                            </button>
                                        </GridField>
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <div style={{ marginTop: "18px" }}>
                            <h4 style={{ marginBottom: "10px", color: COLORS.navy }}>{t("Page Fields")}</h4>
                            {loading.fields ? (
                                <div>{t("Loading fields...")}</div>
                            ) : selectedPage ? (
                                pageFields.length === 0 ? (
                                    <div style={{ color: COLORS.steelGrey }}>{t("No fields defined for this page")}</div>
                                ) : (
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                            <tr>
                                                <th style={theadCellStyle}>{t("Field Name")}</th>
                                                <th style={theadCellStyle}>{t("Display Name")}</th>
                                                <th style={theadCellStyle}>{t("Status")}</th>
                                                <th style={theadCellStyle}>{t("Action")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pageFields.map((field) => (
                                                <tr key={field.id}>
                                                    <td style={tbodyCellStyle}>{field.field_name || field.name}</td>
                                                    <td style={tbodyCellStyle}>{field.display_name || field.field_name || field.name}</td>
                                                    <td style={{ ...tbodyCellStyle, textAlign: "center" }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!field.is_active}
                                                            onChange={() =>
                                                                setPageFields((prev) =>
                                                                    prev.map((f) =>
                                                                        f.id === field.id ? { ...f, is_active: !f.is_active } : f
                                                                    )
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                    <td style={{ ...tbodyCellStyle, textAlign: "center" }}>
                                                        <button
                                                            style={{
                                                                ...buttonStyle,
                                                                background: COLORS.deepGreen,
                                                                color: "#fff",
                                                            }}
                                                            onClick={() => saveField(field)}
                                                            disabled={loading.fieldSaveId === field.id}
                                                        >
                                                            {loading.fieldSaveId === field.id ? t("Saving...") : t("Save")}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )
                            ) : (
                                <div style={{ color: COLORS.steelGrey }}>{t("Please select a page to view its fields")}</div>
                            )}
                        </div>
                    </div>
                )}

                {mode === "role" && (
                    <div style={panelStyle}>
                        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                            <div style={{ minWidth: "260px", flex: "1" }}>
                                <GridField label={t("Role")}>
                                    <SearchableDropdown
                                        options={roleOptions}
                                        value={selectedRole}
                                        onChange={handleRoleChange}
                                        placeholder="Select role"
                                        isLoading={loading.roles}
                                    />
                                </GridField>
                            </div>

                            <div style={{ minWidth: "260px", flex: "1" }}>
                                <GridField label={t("Page")}>
                                    <SearchableDropdown
                                        options={rolePageOptions}
                                        value={selectedRolePage}
                                        onChange={handleRolePageChange}
                                        placeholder="Select page"
                                        isLoading={loading.rolePages}
                                    />
                                </GridField>
                            </div>

                            {rolePageData ? (
                                <>
                                    <div style={{ minWidth: "180px" }}>
                                        <GridField label={t("Page Name")}>
                                            <input
                                                style={inputStyle}
                                                value={rolePageData?.page_name || ""}
                                                disabled={!isEditingRolePage}
                                                onChange={(e) =>
                                                    setRolePageData((prev) => ({ ...prev, page_name: e.target.value }))
                                                }
                                            />
                                        </GridField>
                                    </div>

                                    <div style={{ minWidth: "180px" }}>
                                        <GridField label={t("Display Name")}>
                                            <input
                                                style={inputStyle}
                                                value={rolePageData?.display_name || ""}
                                                disabled={!isEditingRolePage}
                                                onChange={(e) =>
                                                    setRolePageData((prev) => ({ ...prev, display_name: e.target.value }))
                                                }
                                            />
                                        </GridField>
                                    </div>

                                    <div style={{ minWidth: "80px" }}>
                                        <GridField label={t("Status")}>
                                            <div style={{ height: "38px", display: "flex", alignItems: "center" }}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!rolePageData?.is_active}
                                                    disabled={!isEditingRolePage}
                                                    onChange={() =>
                                                        setRolePageData((prev) => ({
                                                            ...prev,
                                                            is_active: !prev?.is_active,
                                                        }))
                                                    }
                                                />
                                            </div>
                                        </GridField>
                                    </div>

                                    <div style={{ minWidth: "110px" }}>
                                        <GridField label={t("Action")}>
                                            <button
                                                style={{
                                                    ...buttonStyle,
                                                    background: isEditingRolePage ? COLORS.lightBlue : COLORS.lightTint,
                                                    color: isEditingRolePage ? "#fff" : COLORS.navy,
                                                    width: "100%",
                                                }}
                                                onClick={isEditingRolePage ? saveRolePage : () => setIsEditingRolePage(true)}
                                            >
                                                {loading.rolePageSave ? t("Saving...") : isEditingRolePage ? t("Save") : t("Edit")}
                                            </button>
                                        </GridField>
                                    </div>
                                </>
                            ) : null}
                        </div>

                        <div style={{ marginTop: "18px" }}>
                            <h4 style={{ marginBottom: "10px", color: COLORS.navy }}>{t("Role Fields")}</h4>
                            {loading.roleFields ? (
                                <div>{t("Loading fields...")}</div>
                            ) : selectedRole && selectedRolePage ? (
                                roleFields.length === 0 ? (
                                    <div style={{ color: COLORS.steelGrey }}>
                                        {t("No role fields defined for this page")}
                                    </div>
                                ) : (
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                            <tr>
                                                <th style={theadCellStyle}>{t("Field Name")}</th>
                                                <th style={theadCellStyle}>{t("Status")}</th>
                                                <th style={theadCellStyle}>{t("Action")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {roleFields.map((field) => {
                                                const fieldName = field.field_name || field.name;
                                                return (
                                                    <tr key={fieldName}>
                                                        <td style={tbodyCellStyle}>{fieldName}</td>
                                                        <td style={{ ...tbodyCellStyle, textAlign: "center" }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={!!field.is_active}
                                                                onChange={() =>
                                                                    setRoleFields((prev) =>
                                                                        prev.map((f) =>
                                                                            (f.field_name || f.name) === fieldName
                                                                                ? { ...f, is_active: !f.is_active }
                                                                                : f
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        </td>
                                                        <td style={{ ...tbodyCellStyle, textAlign: "center" }}>
                                                            <button
                                                                style={{
                                                                    ...buttonStyle,
                                                                    background: COLORS.deepGreen,
                                                                    color: "#fff",
                                                                }}
                                                                onClick={() => saveRoleField(field)}
                                                                disabled={loading.roleFieldSaveName === fieldName}
                                                            >
                                                                {loading.roleFieldSaveName === fieldName ? t("Saving...") : t("Save")}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )
                            ) : (
                                <div style={{ color: COLORS.steelGrey }}>
                                    {t("Please select both role and page to view role-wise fields")}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Sidebar>
    );
}

const theadCellStyle = {
    textAlign: "left",
    padding: "10px 12px",
    background: COLORS.deepGreen,
    color: "#fff",
    fontSize: "12px",
};

const tbodyCellStyle = {
    padding: "10px 12px",
    borderBottom: `1px solid ${COLORS.coolGrey}`,
    fontSize: "13px",
    color: COLORS.navy,
};

export default DeveloperControl;